import { useEffect, useState } from 'react';
import { liveQuery } from 'dexie';
import { db } from '../db';
import { MUTATION_STATUS } from '../services/mutationLog';

/**
 * Live-subscribe to pending mutation_log rows.
 *
 * Automatically updates when rows are added, removed, or transitioned.
 * Consumer should memoize `types` array to avoid re-subscribing on every render.
 *
 * @param {{ types?: string[], entityId?: number|string }} [opts]
 * @returns {{ mutations: object[], isLoading: boolean }}
 */
export function useOverlayedMutations({ types, entityId } = {}) {
  const [mutations, setMutations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Reason: serialize types to a string dep so the effect re-runs on content
  // changes, not reference changes — caller arrays are often literals.
  const typesKey = types?.join(',') ?? '';

  useEffect(() => {
    // Reconstruct the types array inside the effect from the stable string dep.
    const localTypes = typesKey ? typesKey.split(',') : null;

    const sub = liveQuery(() =>
      db.table('mutation_log')
        .where('status').equals(MUTATION_STATUS.PENDING)
        .sortBy('created_at'),
    ).subscribe({
      next: (rows) => {
        let filtered = rows;
        if (localTypes && localTypes.length) {
          const typeSet = new Set(localTypes);
          filtered = filtered.filter((m) => typeSet.has(m.type));
        }
        if (entityId != null) {
          filtered = filtered.filter(
            (m) =>
              m.payload?.article_id === entityId
              || m.payload?.topic_id === entityId
              || (Array.isArray(m.payload?.article_ids) && m.payload.article_ids.includes(entityId)),
          );
        }
        setMutations(filtered);
        setIsLoading(false);
      },
      error: () => {
        setMutations([]);
        setIsLoading(false);
      },
    });
    return () => sub.unsubscribe();
  }, [typesKey, entityId]);

  return { mutations, isLoading };
}

export default useOverlayedMutations;
